import React, { createContext, useContext, useEffect, useRef, useState } from "react";
import { Device } from "@twilio/voice-sdk";
import { useSelector } from "react-redux";

const TwilioDeviceContext = createContext();

// 🧠 Caché de token en memoria de módulo: sobrevive a remounts del Provider
// (p. ej. si el árbol de rutas remonta MainScreen) para no pedir un token
// nuevo si el actual todavía es válido.
let tokenCacheado = null;
let tokenCacheadoExpira = 0; // epoch ms

export const TwilioDeviceProvider = ({ children }) => {
  const { idempleado } = useSelector((store) => store.usuario);

  const urlTokenTwilio = process.env.REACT_APP_URL_TOKEN_TWILIO;

  const deviceRef = useRef(null);
  const activeCallRef = useRef(null);
  const inicializadoRef = useRef(false);

  const [callStatus, setCallStatus] = useState("idle"); // idle | conectando | en_llamada | sonando
  const [incomingCall, setIncomingCall] = useState(null);
  const [numeroTelefonoActivo, setNumeroTelefonoActivo] = useState("");
  const [nombreClienteActivo, setNombreClienteActivo] = useState("");

  // 🔑 Pide un Access Token nuevo al backend, salvo que uno en caché
  // siga vigente (con 15s de margen antes de su expiración real).
  const obtenerToken = async ({ forzar = false } = {}) => {
    const ahora = Date.now();
    if (!forzar && tokenCacheado && tokenCacheadoExpira - ahora > 15000) {
      return tokenCacheado;
    }

    const res = await fetch(
      `${urlTokenTwilio}?identity=${encodeURIComponent(String(idempleado))}`,
      {
        headers: {
          "x-api-key": "MI_API_KEY_SUPER_SEGURA_123456",
          "ngrok-skip-browser-warning": "true",
        },
      }
    );
    const data = await res.json();

    tokenCacheado = data.token;

    const ttlMs = (data.ttl ? data.ttl * 1000 : 60 * 60 * 1000);
    tokenCacheadoExpira = Date.now() + ttlMs;

    return tokenCacheado;
  };

  // 📞 Inicializar el Device de Twilio UNA sola vez por sesión
  useEffect(() => {
    if (!idempleado) return;

    // 🚨 evitar doble inicialización (StrictMode / doble montaje)
    if (inicializadoRef.current) return;
    inicializadoRef.current = true;

    let cancelado = false;

    const inicializarDevice = async () => {
      try {
        const token = await obtenerToken();
        if (cancelado) return;

        const device = new Device(token, {
          logLevel: 1,
          // Avisa 30s antes de que expire (default del SDK son 10s),
          // para tener más margen de renovarlo sin problema.
          tokenRefreshMs: 30000,
        });

        // 🔄 Renovación automática del token antes de que expire
        device.on("tokenWillExpire", async () => {
          try {
            const nuevoToken = await obtenerToken({ forzar: true });
            device.updateToken(nuevoToken);
          } catch (error) {
            console.error("No se pudo renovar el token de Twilio:", error);
          }
        });

        device.on("error", (err) => {
          console.error("Error de Twilio Device:", err);
          setCallStatus("idle");

          // 🛟 Fallback: si por alguna razón tokenWillExpire no alcanzó a
          // dispararse a tiempo y el token ya expiró (20104), renovamos
          // de inmediato en cuanto detectamos el error.
          if (err?.code === 20104) {
            obtenerToken({ forzar: true })
              .then((nuevoToken) => device.updateToken(nuevoToken))
              .catch((e) =>
                console.error("No se pudo renovar el token tras error 20104:", e)
              );
          }
        });

        // 📩 llamada entrante
        device.on("incoming", (call) => {
          activeCallRef.current = call;

          const telefonoCliente = call.customParameters?.get("telefono_cliente");
          const nombreCliente = call.customParameters?.get("nombre_cliente");
          const numero = telefonoCliente || call.parameters?.From || "";
          const nombre = nombreCliente || "";

          setNumeroTelefonoActivo(numero);
          setNombreClienteActivo(nombre);
          setIncomingCall(call);
          setCallStatus("sonando");

          // 🔔 si la ventana no tiene el foco (minimizada, otra pestaña u
          // otra app encima), avisar con una notificación del navegador.
          // document.hidden solo detecta cambio de pestaña; hasFocus()
          // también cubre ventana minimizada o desenfocada.
          const ventanaSinFoco =
            typeof document !== "undefined" &&
            (document.hidden || !document.hasFocus());

          if (
            ventanaSinFoco &&
            "Notification" in window &&
            Notification.permission === "granted"
          ) {
            try {
              const notif = new Notification("Llamada entrante", {
                body: nombre ? `${nombre} — ${numero}` : numero || "Número desconocido",
                icon: "/static/media/imagenes/logomaquinaria.jpeg",
                // tag: "twilio-incoming-call",
                requireInteraction: true,
              });

              notif.onclick = () => {
                window.focus();
                notif.close();
              };
            } catch (err) {
              console.error("No se pudo mostrar la notificación de llamada:", err);
            }
          }

          call.on("accept", () => {
            setIncomingCall(null);
            setCallStatus("en_llamada");
          });

          call.on("disconnect", () => {
            setIncomingCall(null);
            setCallStatus("idle");
            activeCallRef.current = null;
          });

          call.on("cancel", () => {
            setIncomingCall(null);
            setCallStatus("idle");
            activeCallRef.current = null;
          });

          call.on("reject", () => {
            setIncomingCall(null);
            setCallStatus("idle");
            activeCallRef.current = null;
          });

          call.on("error", (err) => {
            console.error("Error en llamada entrante:", err);
            setIncomingCall(null);
            setCallStatus("idle");
            activeCallRef.current = null;
          });
        });

        await device.register();

        deviceRef.current = device;
      } catch (error) {
        console.error("No se pudo inicializar el Device de Twilio:", error);
        inicializadoRef.current = false;
      }
    };

    inicializarDevice();

    return () => {
      cancelado = true;
      // 🚨 si el Device se destruye/reinicia con una llamada activa (p. ej.
      // remount del Provider por navegación), colgarla explícitamente para
      // que no quede "viva" del lado de Twilio/teléfono sin interfaz visible.
      activeCallRef.current?.disconnect();
      activeCallRef.current = null;
      deviceRef.current?.destroy();
      deviceRef.current = null;
      inicializadoRef.current = false;
      setCallStatus("idle");
      setIncomingCall(null);
      setNombreClienteActivo("");
    };
  }, [idempleado]);

  // 🔌 cortar la llamada si se pierde la conexión a internet, o si el
  // usuario cierra/recarga la pestaña, para no dejarla "colgada" del lado
  // del navegador mientras Twilio detecta la desconexión por su cuenta.
  useEffect(() => {
    const cortarLlamadaActiva = () => {
      activeCallRef.current?.disconnect();
    };

    window.addEventListener("beforeunload", cortarLlamadaActiva);
    window.addEventListener("pagehide", cortarLlamadaActiva);
    window.addEventListener("offline", cortarLlamadaActiva);

    return () => {
      window.removeEventListener("beforeunload", cortarLlamadaActiva);
      window.removeEventListener("pagehide", cortarLlamadaActiva);
      window.removeEventListener("offline", cortarLlamadaActiva);
    };
  }, []);

  const iniciarLlamada = async (numeroTelefono) => {
    if (!deviceRef.current || callStatus !== "idle") return;

    setNumeroTelefonoActivo(numeroTelefono);
    setNombreClienteActivo("");
    setCallStatus("conectando");

    try {
      const call = await deviceRef.current.connect({
        params: { To: numeroTelefono },
      });
      activeCallRef.current = call;

      call.on("accept", () => setCallStatus("en_llamada"));
      call.on("disconnect", () => {
        setCallStatus("idle");
        activeCallRef.current = null;
      });
      call.on("error", (err) => {
        console.error("Error en la llamada:", err);
        setCallStatus("idle");
        activeCallRef.current = null;
      });
    } catch (error) {
      console.error("No se pudo iniciar la llamada:", error);
      setCallStatus("idle");
    }
  };

  const colgarLlamada = () => {
    deviceRef.current?.disconnectAll();
    setCallStatus("idle");
    activeCallRef.current = null;
    setNombreClienteActivo("");
  };

  const aceptarLlamada = () => {
    incomingCall?.accept();
  };

  const rechazarLlamada = () => {
    incomingCall?.reject();
    setIncomingCall(null);
    setCallStatus("idle");
    setNombreClienteActivo("");
  };

  return (
    <TwilioDeviceContext.Provider
      value={{
        callStatus,
        incomingCall,
        numeroTelefonoActivo,
        nombreClienteActivo,
        iniciarLlamada,
        colgarLlamada,
        aceptarLlamada,
        rechazarLlamada,
      }}
    >
      {children}
    </TwilioDeviceContext.Provider>
  );
};

// Hook para consumir el contexto
export const useTwilioDevice = () => useContext(TwilioDeviceContext);